



















































import Vue from 'vue'
import Component from 'vue-class-component'
import ModalV2 from '@/components/modals/ModalV2.vue'
import { EZuntaInvoiceType, IFacility, IFacilityBillingSettings, IFacilityMonthlyBilling, IInvoiceCCDetails, IInvoiceEcheckDetails, IInvoiceMailingDetails, ILineItem, IZuntaInvoice } from '@zunta/configurations'
import Api from '@/api'
import Input from '@/components/inputs/Input.vue'
import { makeErrorNotification, makeSuccessNotification } from '@/utils/notification'
import LoaderWrapper from '@/components/layout/LoaderWrapper.vue'
import ScrollView from '@/components/ScrollView.vue'
import Icon from '@/components/Icon.vue'
import moment from 'moment'
import MoneyInputV3 from '@/components/inputs/MoneyInputV3.vue'

@Component({
    components: {
        ModalV2,
        Input,
        LoaderWrapper,
        ScrollView,
        Icon,
        MoneyInputV3
    },
    props: {
        invoiceId: {
            type: Number
        }
    }
})
export default class RegenerateInvoiceModal extends Vue {
    invoice: Partial<IZuntaInvoice> = {}
    isLoading: boolean = false
    lineItemMonth = moment().format('MMMM')
    monthlyBilling: IFacilityMonthlyBilling | null = null
    setting: IFacilityBillingSettings | null = null
    lineItems: ILineItem[] = []
    hasNonValidValue: boolean = false

    get invoices (): IZuntaInvoice[] {
        return this.$store.state.chain.billingInvoices
    }

    get hasInvoiceLineItems () {
        const { lineItems } = this.invoice
        return lineItems && lineItems.length
    }

    get items () {
        return this.hasInvoiceLineItems ? this.invoice.lineItems : this.lineItems
    }

    async mounted () {
        this.isLoading = true
        const invoice = await Api.billing.getInvoiceById(this.$props.invoiceId)
        if (invoice) this.invoice = invoice as IZuntaInvoice
        this.lineItemMonth = moment(this.invoice.invoiceDate).format('MMMM')
        if (this.invoice.lineItems && this.invoice.lineItems.length) {
            this.isLoading = false
            return
        }
        if (this.invoice.type !== EZuntaInvoiceType.MONTHLY) {
            this.setting = await Api.billing.getFacilityBiilingSettings(
                (this.invoice.facility as IFacility).id as number ||
                this.invoice.facility as number
            )
            if (this.invoice.type === EZuntaInvoiceType.MAILING) {
                const monthlyBilling = await Api.billing.getFacilityMonthlyBillingById(
                    (this.invoice.facilityMonthlyBilling as IFacilityMonthlyBilling).id ||
                    this.invoice.facilityMonthlyBilling as number
                )
                if (monthlyBilling) this.monthlyBilling = monthlyBilling as IFacilityMonthlyBilling
            }
        }
        this.getLineItems()
        this.isLoading = false
    }

    async regenerateInvoice () {
        if (this.items && this.items.find(item => !Object.values(item).every(val => val || val === null))) {
            this.hasNonValidValue = true
            return
        }
        this.isLoading = true
        if (this.lineItems.length) {
            this.invoice = {
                ...this.invoice,
                lineItems: this.lineItems.map(item =>
                ({
                    ...item,
                    name: item.name || null,
                    quantity: item.quantity
                        ? new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: item.quantitySymbol === '$' ? 2 : 0
                        }).format(Number(item.quantity))
                        : null,
                    quantitySymbol: item.quantitySymbol || null,
                    price: item.price || null,
                    symbol: item.symbol || null
                }))
            }
        }
        const results = await Api.billing.regenerateInvoice(this.invoice as IZuntaInvoice)
        this.isLoading = false
        if (!results) {
            makeErrorNotification(this, 'Failed to regenerate Invoice')
        } else {
            makeSuccessNotification(this, 'Successfully regenerated Invoice')
            this.$emit('update')
        }
        this.close()
    }

    deleteItem (index: number, isDiscrepancy: boolean) {
        let lineItems = this.hasInvoiceLineItems ? this.invoice.lineItems : this.lineItems
        if (!lineItems) return
        lineItems.splice(index, 1)
        if (isDiscrepancy) {
            this.invoice.isDiscrepancy = false
            this.invoice.discrepancy = 0
        }
    }

    addItem () {
        const lineItems = this.hasInvoiceLineItems ? this.invoice.lineItems : this.lineItems
        lineItems && lineItems.unshift({
            name: '',
            quantity: '',
            quantitySymbol: '',
            price: 0,
            symbol: '',
            total: 0
        })
    }

    getKeyForLineItem (key: string) {
        return key as keyof ILineItem
    }

    getLabel (key: string) {
        switch (key as keyof ILineItem) {
            case 'symbol':
                return 'Price symbol'
            case 'quantitySymbol':
                return 'Quantity symbol'
            default:
                return key.charAt(0).toUpperCase() + key.slice(1)
        }
    }

    close () {
        this.$emit('close')
    }

    getLineItems () {
        switch (this.invoice.type) {
            case EZuntaInvoiceType.CC:
                this.lineItems = this.getCCDetails()
                break
            case EZuntaInvoiceType.ECHECK:
                this.lineItems = this.getEcheckDetails()
                break
            case EZuntaInvoiceType.MAILING:
                this.lineItems = this.getMailingDetails()
                break
            case EZuntaInvoiceType.MONTHLY:
                this.lineItems = this.getMonthlyDetails()
        }
        if (this.invoice.isDiscrepancy) {
            this.lineItems.push({
                name: 'Discrepancy',
                total: this.invoice.discrepancy
            })
        }
    }

    getCCDetails (): ILineItem[] {
        if (!this.setting) return []
        const ccDetails = this.invoice.details as IInvoiceCCDetails
        const { ccFee, chargebackFee, ccVolume, chargebackVolume } = ccDetails
        return [
            {
                name: `${this.lineItemMonth} Credit Card % Fee`,
                quantity: ccVolume.toString(),
                quantitySymbol: '$',
                price: this.setting.CCRate,
                symbol: '%',
                total: ccFee
            },
            {
                name: `${this.lineItemMonth} Credit Card Chargeback Fee`,
                quantity: chargebackVolume.toString(),
                price: this.setting.CCChargebackFee,
                symbol: '$',
                total: chargebackFee
            }
        ]
    }

    getMailingDetails (): ILineItem[] {
        if (!this.setting) return []
        const ccDetails = this.invoice.details as IInvoiceMailingDetails
        const {
            mailingVolume,
            additionalPageVolume,
            returnEnvelopVolume,
            mailingFee,
            additionalPageFee,
            returnEnvelopFee
        } = ccDetails
        return [{
            name: `${this.lineItemMonth} Mailing`,
            quantity: mailingVolume.toString(),
            price: this.setting.mailingPerEnvelop,
            symbol: '$',
            total: mailingFee
        },
        {
            name: `${this.lineItemMonth} Return Envelopes`,
            quantity: returnEnvelopVolume.toString(),
            price: this.setting.returnEnvelop,
            symbol: '$',
            total: returnEnvelopFee
        },
        {
            name: `${this.lineItemMonth} Additional Pages`,
            quantity: additionalPageVolume.toString(),
            price: this.setting.additionalPage,
            symbol: '$',
            total: additionalPageFee
        },
        {
            name: this.monthlyBilling ? this.monthlyBilling.adjustmentTitle : undefined,
            symbol: '$',
            total: this.monthlyBilling ? this.monthlyBilling.adjustmentAmount : undefined
        }]
    }

    getMonthlyDetails (): ILineItem[] {
        return [{
            name: `${this.lineItemMonth} Zunta Monthly Fee`,
            quantity: '1',
            price: this.invoice.totalBilledAmount,
            symbol: '$',
            total: this.invoice.totalBilledAmount
        }]
    }

    getEcheckDetails (): ILineItem[] {
        if (!this.setting) return []
        const ccDetails = this.invoice.details as IInvoiceEcheckDetails
        const { eCheckFee, eCheckVolume, eCheckMoreThan10kFee, eCheckMoreThan10kVolume, pennyDiscrepancy } = ccDetails
        return [
            {
                name: `${this.lineItemMonth} eCheck per Transaction Fee`,
                quantity: eCheckVolume.toString(),
                price: this.setting.eCheckRatePerTransaction,
                symbol: '$',
                total: eCheckFee
            },
            {
                name: `${this.lineItemMonth} eCheck Fee for payments > 10k`,
                quantity: eCheckMoreThan10kVolume.toString(),
                quantitySymbol: '$',
                price: this.setting.eCheckRateForMoreThan10k,
                symbol: '%',
                total: eCheckMoreThan10kFee + (eCheckMoreThan10kFee > 0 ? pennyDiscrepancy : 0)
            }
        ]
    }
}
